import { PaginationState, SortingState } from '@tanstack/react-table'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { TableComponent } from 'components/table/TableComponent'
import { getSensorColor, useScatterplotContext } from 'features/timeSeries'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'

import { SensorScatterplotTableColumns } from './SensorScatterplotTableColumns'

const TableContainer = styled.div`
  width: 100%;
  height: 100%;
`

export const SensorScatterplotTable: React.FC = () => {
  const {
    customSensorColors,
    selectedSensors,
    setSelectedSensors,
    handleYMinChange,
    handleYMaxChange,
    sensorRanges,
    customAxisRanges,
    handleChangeColor,
    autoScaleMode,
  } = useScatterplotContext()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [deletePopupPosition, setDeletePopupPosition] = useState({
    top: 0,
    left: 0,
  })

  const handleRemoveSensor = useCallback(
    (sensorId: string) => {
      setSelectedSensors((prev) =>
        prev.filter((sensor) => sensor.id !== sensorId)
      )
    },
    [setSelectedSensors]
  )

  const handleRemoveAll = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (selectedSensors.length === 0) {
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      setDeletePopupPosition({
        top: rect.bottom + 2,
        left: rect.left + 15,
      })
      setShowConfirmDelete(true)
    },
    [selectedSensors]
  )

  const confirmRemoveAll = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    setSelectedSensors([])
    setShowConfirmDelete(false)
  }

  const cancelRemoveAll = () => {
    setShowConfirmDelete(false)
  }

  const columns = SensorScatterplotTableColumns(
    handleRemoveSensor,
    handleRemoveAll,
    handleYMinChange,
    handleYMaxChange,
    sensorRanges,
    handleChangeColor,
    autoScaleMode
  )

  const data: SensorScatterplotData[] = selectedSensors.map(
    (sensor, sensorIndex) => ({
      id: sensor.id,
      name: sensor.data.name,
      description: sensor.data.description || '',
      yMin:
        customAxisRanges[sensor.id]?.min ??
        Number(sensorRanges[sensor.id].min.toPrecision(4)),
      yMax:
        customAxisRanges[sensor.id]?.max ??
        Number(sensorRanges[sensor.id].max.toPrecision(4)),
      color: customSensorColors[sensor.id] || getSensorColor(sensorIndex),
    })
  )

  return (
    <TableContainer>
      <TableComponent
        data={data}
        columns={columns}
        isLoadingData={false}
        footerName='Tag(s)'
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        showFilterBar={false}
      />
      {showConfirmDelete && (
        <ConfirmDeletePopup
          entityName='sensors'
          deleteMessage='Remove all sensors from the chart?'
          popupPosition={deletePopupPosition}
          cancelDelete={cancelRemoveAll}
          confirmDelete={confirmRemoveAll}
        />
      )}
    </TableContainer>
  )
}
